import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { useParams, useNavigate } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import 'dayjs/locale/uk';
import { fetchTutorStudents, fetchStudentSummary } from '../../api/students';
import { fetchLessons, createLesson, updateLesson, deleteLesson, uploadHomeworkFile, deleteHomeworkFile } from '../../api/lessons';
import useAuthStore from '../../store/authStore';
import LessonFormDialog from '../../components/forms/LessonFormDialog';
import ConfirmDialog from '../../components/feedback/ConfirmDialog';
import { enqueueSnackbar } from 'notistack';
import StudentInfoCard from '../../components/student/StudentInfoCard';
import JournalHeader from '../../components/student/JournalHeader';
import PaymentBadge from '../../components/payment/PaymentBadge';
import LessonScoreCell from '../../components/lessons/LessonScoreCell';
import LessonFormatBadge from '../../components/lessons/LessonFormatBadge';
import LessonFilters from '../../components/lessons/LessonFilters';
import LessonPerformanceChart from '../../components/lessons/LessonPerformanceChart';
import LessonCardMobile from '../../components/lessons/LessonCardMobile';
import HomeworkCell from '../../components/lessons/HomeworkCell';
import ExpandableText from '../../components/common/ExpandableText';
import AssessmentTable from '../../components/lessons/AssessmentTable';
import { exportLessonsToExcel } from '../../utils/exportLessonsToExcel';

dayjs.locale('uk');

const B = { primary: '#4357AD', dark: '#1E2A4A', orange: '#FF521B' };
const cardSx = { borderRadius: 4, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', bgcolor: '#fff' };

const StudentJournalPage = () => {
  const { studentId } = useParams();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNarrow = useMediaQuery('(max-width:900px)');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [topicFilter, setTopicFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(null);

  const studentsQuery = useQuery({
    queryKey: ['tutor-students', user?.id],
    queryFn: () => fetchTutorStudents(user.id),
    enabled: Boolean(user?.id),
  });

  const currentStudent = useMemo(
    () => (studentsQuery.data || []).find((s) => String(s.id) === String(studentId)),
    [studentsQuery.data, studentId]
  );

  // An archived student is no longer in the tutor list, so the journal stays closed for them.
  const hasAccess = Boolean(studentId) && Boolean(currentStudent);

  const lessonsQuery = useQuery({
    queryKey: ['lessons', studentId],
    queryFn: () => fetchLessons(studentId),
    enabled: hasAccess,
  });

  const summaryQuery = useQuery({
    queryKey: ['student-summary', studentId],
    queryFn: () => fetchStudentSummary(studentId),
    enabled: hasAccess,
  });

  const filteredLessons = useMemo(() => {
    const lessons = lessonsQuery.data || [];
    return lessons
      .filter((lesson) => {
        const matchesTopic = topicFilter
          ? (lesson.topic || '').toLowerCase().includes(topicFilter.toLowerCase())
          : true;
        const lessonDate = dayjs(lesson.date).format('YYYY-MM-DD');
        const matchesDateFrom = dateFrom ? lessonDate >= dateFrom : true;
        const matchesDateTo = dateTo ? lessonDate <= dateTo : true;
          return matchesTopic && matchesDateFrom && matchesDateTo;
      })
      .sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf());
  }, [lessonsQuery.data, topicFilter, dateFrom, dateTo]);

  const availableMonths = useMemo(() => {
    const months = new Map();
    (lessonsQuery.data || []).forEach((l) => {
      const d = dayjs(l.date);
      const key = d.format('YYYY-MM');
      if (!months.has(key)) {
        months.set(key, { key, label: d.format('MMM YYYY'), year: d.year(), month: d.month() });
      }
    });
    return [...months.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [lessonsQuery.data]);

  const displayedLessons = useMemo(() => {
    if (!selectedMonth) return filteredLessons;
    return filteredLessons.filter((l) => dayjs(l.date).format('YYYY-MM') === selectedMonth);
  }, [filteredLessons, selectedMonth]);

  const createMutation = useMutation({
    mutationFn: (payload) => createLesson({ ...payload, studentId: Number(studentId) }),
    onSuccess: () => {
      enqueueSnackbar('Урок додано', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['lessons', studentId] });
      queryClient.invalidateQueries({ queryKey: ['student-summary', studentId] });
      queryClient.invalidateQueries({ queryKey: ['schedule-attention-count'] });
    },
    onError: () => enqueueSnackbar('Не вдалося додати урок', { variant: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => updateLesson(id, payload),
    onSuccess: () => {
      enqueueSnackbar('Урок оновлено', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['lessons', studentId] });
      queryClient.invalidateQueries({ queryKey: ['student-summary', studentId] });
      queryClient.invalidateQueries({ queryKey: ['tutor-students'] });
    },
    onError: () => enqueueSnackbar('Не вдалося оновити урок', { variant: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteLesson(id),
    onSuccess: () => {
      enqueueSnackbar('Урок видалено', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['lessons', studentId] });
      queryClient.invalidateQueries({ queryKey: ['student-summary', studentId] });
      queryClient.invalidateQueries({ queryKey: ['schedule-attention-count'] });
      queryClient.invalidateQueries({ queryKey: ['schedule-me'] });
    },
    onError: () => enqueueSnackbar('Не вдалося видалити урок', { variant: 'error' }),
  });

  const syncHomeworkFiles = async (lessonId, pendingFiles, removedFileIds) => {
    if (!pendingFiles.length && !removedFileIds.length) return;
    for (const fileId of removedFileIds) {
      try {
        await deleteHomeworkFile(lessonId, fileId);
      } catch {
        enqueueSnackbar('Не вдалося видалити файл', { variant: 'error' });
      }
    }
    for (const file of pendingFiles) {
      try {
        await uploadHomeworkFile(lessonId, file);
      } catch (err) {
        enqueueSnackbar(`Не вдалося завантажити «${file.name}»: ${err.response?.data?.message || 'помилка'}`, { variant: 'error' });
      }
    }
    // мутація інвалідовує кеш ще до файлових операцій — повторюємо після них
    queryClient.invalidateQueries({ queryKey: ['lessons', studentId] });
  };

  const handleSubmit = async (values, fileOps = {}) => {
    const { pendingFiles = [], removedFileIds = [] } = fileOps;
    if (selectedLesson) {
      await updateMutation.mutateAsync({ id: selectedLesson.id, payload: values });
      await syncHomeworkFiles(selectedLesson.id, pendingFiles, removedFileIds);
    } else {
      const created = await createMutation.mutateAsync(values);
      await syncHomeworkFiles(created.id, pendingFiles, []);
    }
  };

  if (!currentStudent) {
    if (studentsQuery.isLoading) {
      return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: B.primary }} /></Box>;
    }
    return (
      <Stack spacing={2}>
        <Button onClick={() => navigate('/tutor/students')} sx={{ color: B.primary, alignSelf: 'flex-start' }}>Назад до списку</Button>
        <Typography sx={{ color: '#8891A5' }}>Учня не знайдено.</Typography>
      </Stack>
    );
  }

  const isIncomplete = (lesson) => !lesson.topic || (lesson.homeworkScore == null && lesson.activityScore == null);

  return (
    <Stack spacing={2.5}>
      <JournalHeader
        student={currentStudent}
        onBack={() => navigate('/tutor/students')}
        action={
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            sx={{
              bgcolor: '#fff',
              color: B.primary,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.85)' },
              borderRadius: 3,
              px: 3,
              textTransform: 'none',
              fontWeight: 700,
            }}
            onClick={() => { setSelectedLesson(null); setDialogOpen(true); }}
          >
            Додати урок
          </Button>
        }
      />

      <StudentInfoCard student={currentStudent} summary={summaryQuery.data} />

      {/* Lessons table */}
      <Card sx={{ ...cardSx, overflow: 'hidden', border: `2px solid ${B.primary}` }}>
        {lessonsQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: B.primary }} /></Box>
        ) : (
          <>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 2,
              bgcolor: '#F8F7FF',
              borderBottom: '2px solid #EDE9FE',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              alignItems: { md: 'center' },
              justifyContent: 'space-between',
            }}
          >
            <LessonFilters
              topicFilter={topicFilter} onTopicChange={setTopicFilter}
              dateFrom={dateFrom} onDateFromChange={setDateFrom}
              dateTo={dateTo} onDateToChange={setDateTo}
            />
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              disabled={!filteredLessons.length}
              onClick={() => exportLessonsToExcel(filteredLessons, currentStudent.name)}
              sx={{ borderColor: '#EDE9FE', color: B.primary, borderRadius: 3, textTransform: 'none', fontWeight: 600, flexShrink: 0, '&:hover': { borderColor: B.primary, bgcolor: '#EDE9FE' } }}
            >
              Експорт
            </Button>
          </Box>
          {availableMonths.length > 1 && (
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 1,
                display: 'flex',
                gap: 0.5,
                flexWrap: 'wrap',
                borderBottom: '1px solid #EDE9FE',
                bgcolor: '#fff',
              }}
            >
              <Chip
                label="Усі"
                size="small"
                onClick={() => setSelectedMonth(null)}
                sx={{
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: 'pointer',
                  bgcolor: !selectedMonth ? B.primary : '#F3F0FF',
                  color: !selectedMonth ? '#fff' : '#8891A5',
                  '&:hover': { bgcolor: !selectedMonth ? '#3648A0' : '#EDE9FE' },
                }}
              />
              {availableMonths.map((m) => (
                <Chip
                  key={m.key}
                  label={m.label}
                  size="small"
                  onClick={() => setSelectedMonth(m.key)}
                  sx={{
                    fontWeight: 600,
                    fontSize: 12,
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                    bgcolor: selectedMonth === m.key ? B.primary : '#F3F0FF',
                    color: selectedMonth === m.key ? '#fff' : '#8891A5',
                    '&:hover': { bgcolor: selectedMonth === m.key ? '#3648A0' : '#EDE9FE' },
                  }}
                />
              ))}
            </Box>
          )}
          {isNarrow ? (
            <Box sx={{ maxHeight: 620, overflowY: 'auto' }}>
              {displayedLessons.map((lesson, idx) => (
                <LessonCardMobile
                  key={lesson.id}
                  lesson={lesson}
                  rowNum={displayedLessons.length - idx}
                  statusSlot={
                    <>
                      <PaymentBadge paid={lesson.paid} disabled size="md" />
                      {isIncomplete(lesson) && (
                        <Chip
                          label="Заповнити"
                          size="small"
                          sx={{ bgcolor: B.primary, color: '#fff', fontWeight: 600, fontSize: 13, height: 34, borderRadius: 2, cursor: 'pointer', '&:hover': { bgcolor: '#3648A0' } }}
                          onClick={() => { setSelectedLesson(lesson); setDialogOpen(true); }}
                        />
                      )}
                    </>
                  }
                  actionsSlot={
                    <Stack direction="row" spacing={0.25}>
                      <IconButton
                        size="small"
                        aria-label="Редагувати"
                        onClick={() => { setSelectedLesson(lesson); setDialogOpen(true); }}
                        sx={{ color: '#8891A5', '&:hover': { color: '#fff', bgcolor: B.primary } }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="Видалити"
                        onClick={() => setConfirmState(lesson)}
                        sx={{ color: '#8891A5', '&:hover': { color: '#fff', bgcolor: '#EF4444' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  }
                />
              ))}
              {!displayedLessons.length && (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#8891A5' }}>Немає уроків за заданими критеріями.</Typography>
                </Box>
              )}
            </Box>
          ) : (
          <TableContainer sx={{ maxHeight: 620, overflowX: 'auto', overflowY: 'auto' }}>
            <Table stickyHeader sx={{ minWidth: 1080 }}>
              <TableHead>
                <TableRow
                  sx={{
                    '& th': {
                      bgcolor: '#F8F7FF',
                      color: B.primary,
                      fontWeight: 700,
                      fontSize: 12,
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                      textAlign: 'center',
                      borderBottom: '2px solid #EDE9FE',
                      borderRight: '1px solid #EDE9FE',
                      py: 1.8,
                      px: 2.5,
                      '&:last-child': { borderRight: 'none' },
                    },
                  }}
                >
                  <TableCell sx={{ width: 50 }}>#</TableCell>
                  <TableCell>Дата</TableCell>
                  <TableCell>Тема</TableCell>
                  <TableCell>Примітки</TableCell>
                  <TableCell>Домашнє завдання</TableCell>
                  <TableCell align="center">Оцінка</TableCell>
                  <TableCell align="center">Статус</TableCell>
                  <TableCell align="right">Дії</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedLessons.map((lesson, idx) => {
                  const incomplete = isIncomplete(lesson);
                  const rowNum = displayedLessons.length - idx;
                  return (
                    <TableRow
                      key={lesson.id}
                      sx={{
                        transition: 'all 0.15s',
                        bgcolor: idx % 2 === 0 ? '#fff' : '#FAFAFF',
                        '&:hover': { bgcolor: '#EDE9FE' },
                        '& td': {
                          py: { xs: 1, md: 2 },
                          px: { xs: 1, md: 2.5 },
                          borderBottom: '1px solid #F1F0FB',
                          borderRight: '1px solid #F1F0FB',
                          '&:last-child': { borderRight: 'none' },
                        },
                      }}
                    >
                      <TableCell>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 2,
                            bgcolor: B.primary,
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            fontSize: 13,
                          }}
                        >
                          {String(rowNum).padStart(2, '0')}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Stack>
                          <Typography variant="body2" fontWeight={700} sx={{ color: B.dark }}>
                            {dayjs(lesson.date).format('DD.MM.YYYY')}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#8891A5' }}>
                            {dayjs(lesson.date).format('dddd')}
                            {lesson.scheduleStartTime && (
                              <>, {dayjs(lesson.scheduleStartTime).format('HH:mm')}</>
                            )}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography lang="uk" variant="body2" fontWeight={700} sx={{ color: lesson.topic ? B.dark : '#8891A5', fontStyle: lesson.topic ? 'normal' : 'italic', hyphens: 'auto', overflowWrap: 'break-word', wordBreak: 'normal' }}>
                          {lesson.topic || 'Не заповнено'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260, minWidth: 200 }}>
                        {lesson.notes ? (
                          <ExpandableText
                            lines={4}
                            sx={{ bgcolor: '#F8F7FF', border: '1px solid #EDE9FE', borderRadius: 2, px: 1.5, py: 0.75 }}
                          >
                            {lesson.notes}
                          </ExpandableText>
                        ) : (
                          <Typography variant="body2" sx={{ color: '#C4B5FD', fontStyle: 'italic' }}>
                            Без приміток
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260, minWidth: 200 }}>
                        <HomeworkCell lesson={lesson} />
                      </TableCell>
                      <TableCell align="center">
                        <LessonScoreCell lesson={lesson} />
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                          <PaymentBadge paid={lesson.paid} disabled />
                          {incomplete && (
                            <Chip
                              label="Заповнити"
                              size="small"
                              sx={{
                                bgcolor: B.primary,
                                color: '#fff',
                                fontWeight: 600,
                                fontSize: 11,
                                height: 24,
                                cursor: 'pointer',
                                '&:hover': { bgcolor: '#3648A0' },
                              }}
                              onClick={() => { setSelectedLesson(lesson); setDialogOpen(true); }}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                          <Tooltip title="Редагувати">
                            <IconButton
                              size="small"
                              onClick={() => { setSelectedLesson(lesson); setDialogOpen(true); }}
                              sx={{
                                color: '#8891A5',
                                '&:hover': { color: '#fff', bgcolor: B.primary },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Видалити">
                            <IconButton
                              size="small"
                              onClick={() => setConfirmState(lesson)}
                              sx={{
                                color: '#8891A5',
                                '&:hover': { color: '#fff', bgcolor: '#EF4444' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!displayedLessons.length && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, borderBottom: 'none' }}>
                      <Typography variant="body2" sx={{ color: '#8891A5' }}>Немає уроків за заданими критеріями.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
          </>
        )}
      </Card>

      <AssessmentTable studentId={studentId} canEdit />

      <LessonPerformanceChart lessons={filteredLessons} />

      <LessonFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
        initialValues={{
          date: selectedLesson?.date || dayjs().format('YYYY-MM-DD'),
          topic: selectedLesson?.topic || '',
          notes: selectedLesson?.notes || '',
          homeworkScore: selectedLesson?.homeworkScore ?? '',
          activityScore: selectedLesson?.activityScore || '',
          behaviorScore: selectedLesson?.behaviorScore || '',
          comprehensionScore: selectedLesson?.comprehensionScore || '',
          homeworkTask: selectedLesson?.homeworkTask || '',
          homeworkFiles: selectedLesson?.homeworkFiles || [],
          paid: selectedLesson?.paid || false,
        }}
        title={selectedLesson ? 'Редагувати урок' : 'Новий урок'}
      />

      <ConfirmDialog
        open={Boolean(confirmState)}
        title="Точно бажаєте видалити заняття?"
        description="Цю дію відмінити неможливо."
        onCancel={() => setConfirmState(null)}
        onConfirm={async () => {
          await deleteMutation.mutateAsync(confirmState.id);
          setConfirmState(null);
        }}
      />
    </Stack>
  );
};

export default StudentJournalPage;
